import { Fragment, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Area, Brush, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import styles from './styles.module.scss';
import { ReactComponent as TravellerIcon } from './assets/TravellerIcon.svg';

import { ChartActiveDot, ChartLegend, ChartTooltip } from './index.jsx';
import { downloadCSV } from '../../utils/charts';
import { GetChartsMap, GetDewTicks, GetGddTicks, GetTemperatureTicks } from './lib/index.jsx';
import { DateFormat } from '../../constants/data';
import { BreakpointMobile } from '../../constants/ui';
import { ChartColorsMap, ChartHeight, ChartHeightMobile, ChartYAxisWidth } from './constants/index.jsx';
import { useViewport } from '../../hooks';
import { convertMinutesToHours, convertMinutesToMinutes } from '../../utils/dates';

const ChartTooltipTemplate = ({ active, payload, label, sensors, charts, ChartsMap, t }) => {
  if (!active || !payload || payload.length < 1) return null;
  const payloadData = payload[0].payload;

  return (
    <ChartTooltip label={label} sensors={sensors} data={payloadData} chartsMap={ChartsMap} charts={charts} t={t} />
  );
};

const Chart = ({ t, sensors, charts = [], data, period, depth }) => {
  const { widthViewport } = useViewport();
  const ChartsMap = GetChartsMap(t);

  const [dataCsv, setDataCsv] = useState([]);
  const [domainYAsis, setDomainYAsis] = useState(null);
  const [domainYAsis2, setDomainYAsis2] = useState(null);
  const [brushIndex, setBrushIndex] = useState([0, 0]);
  const [ticksYAsis, setTicksYAsis] = useState(null);
  const [ticksYAsis2, setTicksYAsis2] = useState(null);
  const [updateChart, setUpdateChart] = useState(false);

  // Merge data for 2 charts
  const mergedData = useMemo(() => {
    if (!data || !data.length || data.length < 2) return;

    const map1 = new Map(data[0].map((i) => [i.date, i]));
    const map2 = new Map(data[1].map((i) => [i.date, i]));

    const allDates = Array.from(new Set([...data[0].map((i) => i.date), ...data[1].map((i) => i.date)])).sort(
      (a, b) => new Date(a) - new Date(b),
    );

    return allDates.map((date) => {
      const item1 = map1.get(date);
      const item2 = map2.get(date);

      const obj = {
        date,
        value: item1?.value ?? null,
        area: item1?.area ?? [],
        value2: item2?.value ?? null,
        area2: item2?.area ?? [],
      };

      return sensors.length === 1
        ? obj
        : {
            ...obj,
            valueCompared: item1?.valueCompared ?? null,
            areaCompared: item1?.areaCompared ?? [],
            value2Compared: item2?.valueCompared ?? null,
            area2Compared: item2?.areaCompared ?? [],
          };
    });
  }, [data]);

  const GetTicksFormat = (value, id) => {
    switch (id) {
      case 'temperature':
        return value > 0 ? `+${value}` : `${value}`;
      case 'dew':
        return domainYAsis && domainYAsis.length > 0 && domainYAsis[1] < 60
          ? convertMinutesToMinutes(value)
          : convertMinutesToHours(value);
      default:
        break;
    }
    return value;
  };

  // Csv, Y asis data
  useEffect(() => {
    if (!data || (data.length > 1 && !mergedData)) return;

    const list = data.length > 1 ? mergedData : data[0];

    const csvData = [];
    const minArray = [0];
    const maxArray = [0];
    const values = [];
    const values2 = [];
    const minArray2 = [0];
    const maxArray2 = [0];

    list.map((item) => {
      if (item.area && item.area.length > 0) {
        minArray.push(item.area[0]);
        maxArray.push(item.area[1]);
      }
      if (item.value) {
        values.push(item.value);
      }

      if (item.area2) {
        minArray2.push(item.area2[0]);
        maxArray2.push(item.area2[1]);
      }
      if (item.value2) {
        values2.push(item.value2);
      }

      csvData.push(
        item.area
          ? {
              date: dayjs(item.date).format(DateFormat),
              value: item.value,
              area: item.area,
            }
          : {
              date: dayjs(item.date).format(DateFormat),
              value: item.value,
            },
      );
    });

    setDataCsv(csvData);

    // Y asis data
    const minY = Math.min(...minArray);
    const maxY = Math.max(...maxArray);
    const minY2 = Math.min(...minArray2);
    const maxY2 = Math.max(...maxArray2);

    let ticks;
    charts.map((chart, index) => {
      switch (chart) {
        case 'temperature':
          if (index === 0) {
            setTicksYAsis(GetTemperatureTicks({ min: minY, max: maxY, steps: 5 }));
            setDomainYAsis([minY, maxY]);
          } else {
            setTicksYAsis2(GetTemperatureTicks({ min: minY2, max: maxY2, steps: 5 }));
            setDomainYAsis2([minY2, maxY2]);
          }
          break;
        case 'dew':
          ticks = GetDewTicks({ values, steps: 5 });
          setTicksYAsis(ticks.ticks);
          setDomainYAsis([ticks.min, ticks.max]);
          break;
        case 'gdd':
          if (index === 0) {
            ticks = GetGddTicks({ values, steps: 5 });
            setTicksYAsis(ticks.ticks);
            setDomainYAsis([ticks.min, ticks.max]);
          } else {
            ticks = GetGddTicks({ values: values2, steps: 5 });
            setTicksYAsis2(ticks.ticks);
            setDomainYAsis2([ticks.min, ticks.max]);
          }
          break;
        case 'bbch':
        case 'humidity':
          index === 0 ? setDomainYAsis([0, 100]) : setDomainYAsis2([0, 100]);
          break;
        case 'ndvi':
          index === 0 ? setDomainYAsis([0, 1]) : setDomainYAsis2([0, 1]);
          break;
        default:
          setTicksYAsis(null);
          setTicksYAsis2(null);
          setDomainYAsis(null);
          setDomainYAsis2(null);
      }
    });
  }, [data]);

  useEffect(() => {
    if (!data || !period || !data[0].length) return;

    const end = data[0].length - 1;
    const start = end > period ? end - period : 0;

    setBrushIndex([start, end]);
    setUpdateChart(true);
  }, [period]);

  if (!data || !ChartsMap || !charts.length) {
    return null;
  }

  return (
    <>
      <div className={styles.chartContainer}>
        {/*Legend*/}
        <ChartLegend
          id={charts[0]}
          id2={charts.length > 1 ? charts[1] : null}
          sensors={sensors}
          title={`${ChartsMap[charts[0]].title}${depth && depth[0] ? ' (' + depth[0] + ' cm)' : ''}`}
          color={ChartsMap[charts[0]].color}
          units={ChartsMap[charts[0]].units}
          title2={
            charts.length > 1
              ? `${ChartsMap[charts[1]].title}${depth && depth[1] ? ' (' + depth[1] + ' cm)' : ''}`
              : null
          }
          color2={charts.length > 1 ? ChartsMap[charts[1]].colorCompared : null}
          units2={charts.length > 1 ? ChartsMap[charts[1]].units : null}
          onDownloadCsv={() =>
            downloadCSV({
              data: dataCsv,
              keys: ChartsMap[charts[0]].csvKeys,
              fileName: `cropler_${sensors[0]}_${ChartsMap[charts[0]].csvFileName}`,
            })
          }
          widthViewport={widthViewport}
        />

        <ResponsiveContainer width="100%" height={widthViewport > BreakpointMobile ? ChartHeight : ChartHeightMobile}>
          <ComposedChart
            width={800}
            height={widthViewport > BreakpointMobile ? ChartHeight : ChartHeightMobile}
            data={mergedData || data[0]}
            syncId={charts.length > 1 ? `${charts[0]}_${charts[1]}_id` : `${charts[0]}_id`}
          >
            <defs>
              {(charts[0] === ChartsMap.dew.id ||
                charts[0] === ChartsMap.humidity.id ||
                charts[0] === ChartsMap.gdd.id) && (
                <>
                  <linearGradient id={`chartGradient${charts[0]}`} x1="0" y1="0" x2="0" y2="1">
                    <stop stopColor={ChartsMap[charts[0]].color} />
                    <stop offset="1" stopColor={ChartsMap[charts[0]].color} stopOpacity="0" />
                  </linearGradient>
                  {charts.length > 1 && (
                    <linearGradient id={`chartGradient${charts[1]}`} x1="0" y1="0" x2="0" y2="1">
                      <stop stopColor={ChartsMap[charts[1]].colorCompared} />
                      <stop offset="1" stopColor={ChartsMap[charts[1]].colorCompared} stopOpacity="0" />
                    </linearGradient>
                  )}
                  {sensors.length === 2 && (
                    <>
                      <linearGradient id={`chartGradientCompared${charts[0]}`} x1="0" y1="0" x2="0" y2="1">
                        <stop stopColor={ChartsMap[charts[0]].color} />
                        <stop offset="1" stopColor={ChartsMap[charts[0]].color} stopOpacity="0" />
                      </linearGradient>
                      {charts.length > 1 && (
                        <linearGradient id={`chartGradientCompared${charts[1]}`} x1="0" y1="0" x2="0" y2="1">
                          <stop stopColor={ChartsMap[charts[1]].colorCompared} />
                          <stop offset="1" stopColor={ChartsMap[charts[1]].colorCompared} stopOpacity="0" />
                        </linearGradient>
                      )}
                    </>
                  )}
                </>
              )}
            </defs>
            <XAxis dataKey="date" tickLine={false} minTickGap={60} height={50} tickSize={12} />
            <YAxis
              yAxisId={`${charts[0]}_left`}
              width={ChartYAxisWidth}
              {...(domainYAsis && { domain: domainYAsis })}
              {...(ticksYAsis && { ticks: ticksYAsis })}
              tickCount={5}
              tickSize={0}
              tickMargin={10}
              horizontal={false}
              tickFormatter={(value) => GetTicksFormat(value, charts[0])}
            />
            <YAxis
              yAxisId={`${charts[0]}_right`}
              orientation="right"
              width={ChartYAxisWidth}
              {...(charts.length === 1 && domainYAsis ? { domain: domainYAsis } : { domain: domainYAsis2 })}
              {...(charts.length === 1 && ticksYAsis ? { ticks: ticksYAsis } : { ticks: ticksYAsis2 })}
              tickCount={5}
              tickSize={0}
              tickMargin={10}
              tickFormatter={(value) => GetTicksFormat(value, charts.length > 1 ? charts[1] : charts[0])}
            />

            {charts.map((id, index) => {
              const color = index === 0 ? ChartsMap[id].color : ChartsMap[id].colorCompared;
              return (
                <Fragment key={id}>
                  <Tooltip
                    content={<ChartTooltipTemplate sensors={sensors} charts={charts} ChartsMap={ChartsMap} t={t} />}
                  />

                  {/*Temperature, NDVI, Leaf NDVI, Soil NDVI - (min, max)*/}
                  {updateChart &&
                    sensors &&
                    charts.length === 1 &&
                    sensors.length === 1 &&
                    (id === ChartsMap.temperature.id ||
                      id === ChartsMap.ndvi.id ||
                      id === ChartsMap.soilNdvi.id ||
                      id === ChartsMap.leafNdvi.id) && (
                      <Area
                        yAxisId={index === 0 ? `${charts[0]}_left` : `${charts[0]}_right`}
                        type="monotone"
                        dataKey={index === 0 ? 'area' : 'area2'}
                        strokeWidth={1}
                        stroke="ccc"
                        fillOpacity={0.3}
                        fill="#ccc"
                        activeDot={false}
                      />
                    )}

                  {/*Humidity, Dew Point, GDD*/}
                  {(id === ChartsMap.humidity.id || id === ChartsMap.dew.id || id === ChartsMap.gdd.id) && (
                    <>
                      <Area
                        yAxisId={index === 0 ? `${charts[0]}_left` : `${charts[0]}_right`}
                        type="monotone"
                        dataKey={index === 0 ? 'value' : 'value2'}
                        stroke={color}
                        strokeWidth={2}
                        fillOpacity={0.2}
                        fill={index === 0 ? `url(#chartGradient${charts[0]})` : `url(#chartGradient${charts[1]})`}
                        activeDot={ChartActiveDot(color)}
                      />
                      {sensors.length > 1 && (
                        <Area
                          yAxisId={index === 0 ? `${charts[0]}_left` : `${charts[0]}_right`}
                          type="monotone"
                          dataKey={index === 0 ? 'valueCompared' : 'value2Compared'}
                          stroke={color}
                          strokeWidth={2}
                          fillOpacity={0.2}
                          strokeDasharray="3 3"
                          fill={
                            index === 0
                              ? `url(#chartGradientCompared${charts[0]})`
                              : `url(#chartGradientCompared${charts[1]})`
                          }
                          activeDot={ChartActiveDot(color)}
                        />
                      )}
                    </>
                  )}

                  {/*BBCH*/}
                  {id === ChartsMap.bbch.id && (
                    <>
                      <Line
                        dataKey={index === 0 ? 'value' : 'value2'}
                        yAxisId={index === 0 ? `${charts[0]}_left` : `${charts[0]}_right`}
                        type="monotone"
                        strokeWidth={2}
                        stroke={color}
                        activeDot={ChartActiveDot(color)}
                      />
                      {sensors.length > 1 && (
                        <Line
                          dataKey={index === 0 ? 'valueCompared' : 'value2Compared'}
                          yAxisId={index === 0 ? `${charts[0]}_left` : `${charts[0]}_right`}
                          type="monotone"
                          strokeWidth={2}
                          strokeDasharray="3 3"
                          stroke={color}
                          activeDot={ChartActiveDot(color)}
                        />
                      )}
                    </>
                  )}

                  {/*Leaf NDVI, Soil NDVI, Leaf Area, Temperature, NDVI*/}
                  {(id === ChartsMap.leafNdvi.id ||
                    id === ChartsMap.soilNdvi.id ||
                    id === ChartsMap.leafArea.id ||
                    id === ChartsMap.temperature.id ||
                    id === ChartsMap.soilHumidity.id ||
                    id === ChartsMap.soilTemperature.id ||
                    id === ChartsMap.salinity.id ||
                    id === ChartsMap.ndvi.id) && (
                    <>
                      <Line
                        dataKey={index === 0 ? 'value' : 'value2'}
                        yAxisId={index === 0 ? `${charts[0]}_left` : `${charts[0]}_right`}
                        type="monotone"
                        strokeWidth={2}
                        stroke={color}
                        activeDot={ChartActiveDot(color)}
                      />
                      {sensors.length > 1 && (
                        <Line
                          dataKey={index === 0 ? 'valueCompared' : 'value2Compared'}
                          yAxisId={index === 0 ? `${charts[0]}_left` : `${charts[0]}_right`}
                          type="monotone"
                          strokeWidth={2}
                          stroke={color}
                          strokeDasharray="3 3"
                          activeDot={ChartActiveDot(color)}
                        />
                      )}
                    </>
                  )}

                  <CartesianGrid strokeDasharray="0 0" stroke={'var(--neutral-8)'} vertical={false} />
                </Fragment>
              );
            })}
            <Brush
              height={40}
              stroke={ChartColorsMap.brush}
              dataKey={'date'}
              startIndex={brushIndex[0]}
              endIndex={brushIndex[1]}
              alwaysShowText={true}
              traveller={<TravellerIcon />}
              travellerWidth={14}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

export default Chart;
